import React, { useEffect, useMemo, useRef } from "react"

import {
  Language
} from "@mui/icons-material"
import { Box, styled, Typography } from "@mui/material"
import { inputClasses } from "@mui/material/Input"
import { menuClasses } from "@mui/material/Menu"
import MuiMenuItem, { menuItemClasses } from "@mui/material/MenuItem"
import MuiSelect, { selectClasses } from "@mui/material/Select"

import { useCustomerInfo } from "@cirrus/common/CustomerInfo"
import { LanguageCode, useTranslations } from "@cirrus/core/i18n"

export const LanguageSelector = () => {
  const selectRef = useRef<HTMLDivElement>(null)
  const customerInfo = useCustomerInfo()
  const { language, changeLanguage } = useTranslations()

  useEffect(() => {
    const nativeInput = selectRef.current?.querySelector<HTMLInputElement>(".MuiSelect-nativeInput")
    if (nativeInput) nativeInput.setAttribute("type", "hidden")
  }, [])

  const languages = useMemo(() => {
    const availableLanguages = customerInfo.data?.availableLanguages ?? {}

    return Object.keys(availableLanguages ?? {}).map((languageCode) => ({
      value: languageCode,
      title: availableLanguages[languageCode] ?? ""
    }))
  }, [customerInfo.data?.availableLanguages])

  return <Select
    ref={selectRef}
    disableUnderline
    id="language-selector-id"
    className={"language-selector"}
    variant="standard"
    defaultValue={language ?? languages[0].value}
    value={language}
    inputProps={{
      "aria-label": "Language selector",
    }}
    renderValue={(selected) => {
      const selectedLanguage = languages.find(({ value }) => value === selected)
      if (!selectedLanguage) return null

      return <Box display="flex" alignItems="center" columnGap="1rem">
        <Language sx={{ fontSize: "20px", color: "#00287A" }} />
        <Typography fontWeight={700} lineHeight="28px" textTransform={"capitalize"} sx={{ color: "#00287A" }}>
          {selectedLanguage.title}
        </Typography>
      </Box>
    }}
    IconComponent={() => null}
    onChange={({ target }) => changeLanguage(target.value as LanguageCode)}
    MenuProps={{
      elevation: 0,
      disableAutoFocusItem: true,
      disablePortal: true
    }}
  >
    {languages.map(({ value, title }) => <MenuItem
      disableRipple
      disabled={value === language}
      key={value}
      value={value}
      sx={{
        borderRadius: "2px"
      }}
    >
      <Typography fontWeight={600} lineHeight="24px" sx={{ color: "#1A1A1A" }}>
        {title}
      </Typography>
    </MenuItem>)}
  </Select>
}

const Select = styled(MuiSelect)(({ theme }) => ({

  gridColumn: 2,
  gridRow: 2,
  justifySelf: "center",
  marginTop: "10px",

  "svg, p": {
    fontSize: "14px",
  },

  [theme.breakpoints.down("md")]: {
    order: 3,
  },

  [`& .${selectClasses.select}.${inputClasses.input}`]: {
    padding: "0 !important",
    minHeight: "auto"
  },
  [`& .${menuClasses.paper}`]: {
    boxShadow: "0px 1px 20px 0px rgba(0, 0, 0, 0.10)",
    ...theme.palette.mode === "light" && { border: "1px solid #EBEBEB" }
  },
  [`& .${menuClasses.list}`]: {
    display: "grid",
    padding: "1.6rem .8rem",
    gap: ".8rem"
  }
}))

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  padding: "0 1rem",
  [`&.${menuItemClasses.selected}.${menuItemClasses.disabled}`]: {
    background: "none"
  },
  "&:hover": {
    ...theme.palette.mode === "light" && { background: "#F2F2F2" }
  }
}))

